import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listPublishedProducts } from '@/services/products';
import type { Product } from '@/domain/types';

const typeColors: Record<string, string> = {
  plugin: 'bg-brand-50 text-brand-700 border-brand-100',
  theme: 'bg-amber-50 text-amber-700 border-amber-100',
  template: 'bg-sky-50 text-sky-700 border-sky-100',
  addon: 'bg-violet-50 text-violet-700 border-violet-100',
  asset: 'bg-rose-50 text-rose-700 border-rose-100',
  app: 'bg-teal-50 text-teal-700 border-teal-100',
};

function CardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-surface-200 p-6 animate-pulse">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-surface-200 shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-surface-200 rounded w-1/3" />
          <div className="h-5 bg-surface-200 rounded w-2/3" />
          <div className="h-3 bg-surface-200 rounded w-full" />
          <div className="h-3 bg-surface-200 rounded w-4/5" />
        </div>
      </div>
      <div className="mt-5 flex gap-2">
        <div className="h-8 bg-surface-200 rounded-lg w-28" />
        <div className="h-8 bg-surface-200 rounded-lg w-24" />
      </div>
    </div>
  );
}

export default function DocsIndexPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    listPublishedProducts()
      .then((all) => setProducts(all.filter((p) => p.docsHtml)))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = search.trim()
    ? products.filter(
        (p) =>
          p.name.toLowerCase().includes(search.toLowerCase()) ||
          p.shortDescription.toLowerCase().includes(search.toLowerCase())
      )
    : products;

  return (
    <div className="min-h-screen bg-surface-50 pt-20 md:pt-24 pb-16">

      {/* Hero */}
      <div className="bg-white border-b border-surface-200 mb-10">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-50 border border-brand-100 text-brand-600 text-xs font-medium mb-5">
            <i className="ri-book-2-line" /> Documentation
          </div>
          <h1 className="text-2xl md:text-4xl font-bold text-surface-900 mb-4">
            Product Documentation
          </h1>
          <p className="text-surface-500 text-sm md:text-base max-w-xl mx-auto mb-8">
            Step-by-step guides, API references, and troubleshooting resources for every Motionsdio product.
          </p>
          <div className="relative max-w-md mx-auto">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-surface-400 text-base" />
            <input
              type="text"
              placeholder="Search documentation..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white shadow-sm"
            />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-6">

        {/* Stats row */}
        {!loading && products.length > 0 && (
          <div className="flex items-center gap-2 mb-6 text-sm text-surface-500">
            <i className="ri-book-open-line text-brand-400" />
            <span>
              {filtered.length} {filtered.length === 1 ? 'guide' : 'guides'} available
              {search && ` for "${search}"`}
            </span>
          </div>
        )}

        {/* Cards */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-surface-200">
            <div className="w-16 h-16 rounded-2xl bg-surface-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-book-2-line text-2xl text-surface-400" />
            </div>
            <h3 className="font-semibold text-surface-900 mb-2">
              {search ? 'No results found' : 'No documentation yet'}
            </h3>
            <p className="text-sm text-surface-500 max-w-xs mx-auto mb-5">
              {search
                ? `No docs match "${search}". Try a different search term.`
                : 'Documentation pages will appear here once they are published.'}
            </p>
            {search && (
              <button onClick={() => setSearch('')} className="text-sm text-brand-600 hover:underline">
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {filtered.map((product) => (
              <div
                key={product.id}
                className="group bg-white rounded-xl border border-surface-200 hover:border-brand-300 hover:shadow-md transition-all duration-200 overflow-hidden flex flex-col"
              >
                {/* Card header with thumbnail strip */}
                {product.thumbnail && (
                  <div className="h-32 overflow-hidden">
                    <img
                      src={product.thumbnail}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}

                <div className="p-5 md:p-6 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border capitalize ${typeColors[product.type] || 'bg-surface-100 text-surface-600 border-surface-200'}`}>
                        {product.type}
                      </span>
                      <span className="text-xs text-surface-400 font-mono">v{product.version}</span>
                    </div>
                  </div>

                  <h2 className="text-base font-bold text-surface-900 mb-2 group-hover:text-brand-600 transition-colors">
                    {product.name}
                  </h2>
                  <p className="text-sm text-surface-500 leading-relaxed mb-4 line-clamp-2 flex-1">
                    {product.shortDescription}
                  </p>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Link
                      to={`/docs/${product.slug}`}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold transition-colors"
                    >
                      <i className="ri-book-2-line" /> View Docs
                    </Link>
                    <Link
                      to={`/product/${product.slug}`}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-surface-300 hover:border-surface-400 text-surface-600 text-xs font-medium transition-colors"
                    >
                      <i className="ri-store-2-line" /> Product Page
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        {!loading && (
          <div className="mt-14 bg-gradient-to-br from-brand-600 to-brand-700 rounded-2xl p-8 md:p-10 text-center text-white">
            <h3 className="text-xl md:text-2xl font-bold mb-3">Need help getting started?</h3>
            <p className="text-brand-100 text-sm md:text-base mb-6 max-w-md mx-auto">
              Browse our products and get expert support directly from the developers.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate('/products')}
                className="px-5 py-2.5 rounded-lg bg-white text-brand-700 font-semibold text-sm transition-colors hover:bg-brand-50 whitespace-nowrap"
              >
                Browse Products
              </button>
              <a
                href="https://motionsdio.com/support"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 rounded-lg border border-brand-400 hover:border-white text-white font-medium text-sm transition-colors whitespace-nowrap"
              >
                Contact Support
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
